/**
 * Observable status for the local-gateway (on-prem node) image path, plus the
 * support-facing diagnostic override.
 *
 * ./index decides, per image, whether the bytes come from the clinic's onsite
 * node over the LAN or from the cloud. Every input to that decision used to be
 * a private module Map with no change notification, so nothing outside the data
 * path could say which way it went: a missing certificate, a clinic firewall
 * and a dismissed Chrome permission all presented as "it feels slow". This
 * module is the single source of truth for that decision's *state*, shaped for
 * a `useSyncExternalStore` consumer (see hooks/use-local-gateway-status.ts).
 *
 * It deliberately imports nothing from ./index — the dependency runs one way
 * (index -> status), so the data path can record transitions without this file
 * dragging the probe machinery into the React tree.
 *
 * Everything here is per-window by design. A popout viewer is a separate
 * JavaScript realm with its own probe, its own verdict and its own override, so
 * two windows can legitimately disagree. That is correct: reachability is a
 * property of a browsing context, not of the clinic.
 */
/**
 * sessionStorage, not localStorage: a diagnostic that outlives the tab it was
 * set in is a diagnostic that silently degrades someone's next session.
 *
 * One sessionStorage behaviour has to be worked around, though. A same-origin
 * window opened *with an opener* starts life with a COPY of its opener's session
 * storage — the same browser rule lib/studyNavigation.ts works around for the
 * worklist return record. Without the check below, pinning the image path in the
 * worklist tab and then popping out a viewer would hand that pin to a window
 * nobody pinned, which is the exact opposite of what this control promises.
 */
const OVERRIDE_STORAGE_KEY = 'newlumen_local_gateway_override';
/**
 * How this document came to be, per Navigation Timing. `reload` and
 * `back_forward` mean the window is returning to a document it already had;
 * `navigate` means a fresh load, which is what a just-opened popup sees.
 * Undefined where the entry is unavailable, which is treated as "not a reload".
 */
function navigationType() {
    const [entry] = performance.getEntriesByType('navigation');
    return entry?.type;
}
/**
 * True when the override sitting in storage was written by a DIFFERENT window.
 *
 * Two facts together are the test: another window opened this one (only then is
 * the storage area a copy — a `noopener` popup gets an empty one), and this is
 * that window's first document rather than a reload of it. The reload case is
 * precisely what the storage key exists to survive, so the two have to be told
 * apart, and the obvious place to stamp a per-window token — `window.name` — is
 * already load-bearing here: multiScreenService targets popups by name, and
 * useCockpitFullscreen and ViewerShell's span index read it back.
 *
 * Where this is wrong, it is wrong in the safe direction: a full cross-document
 * navigation inside a popup drops a pin that popup set itself, costing a support
 * person one click. The other mistake silently changes the image path of a
 * window nobody touched.
 */
function overrideBelongsToAnotherWindow() {
    if (!window.opener)
        return false;
    const type = navigationType();
    return type !== 'reload' && type !== 'back_forward';
}
/**
 * Reduce every origin's phase to the one state the window reports.
 *
 * Order is the whole design:
 *  1. An override wins outright — that is what support asked it for.
 *  2. Nothing offered means cloud, and that is not a fault.
 *  3. One reachable node is enough to say "local": the bytes really are
 *     arriving over the LAN, even if a second origin is still settling.
 *  4. `probing` outranks every negative verdict, because the first images of a
 *     study legitimately come from the cloud while a probe is in flight. This
 *     is the rule that keeps a healthy clinic from flashing "something is
 *     wrong" on every study open.
 *  5. A browser refusal is reported ahead of a plain failure: it is the more
 *     specific diagnosis and the only one with an action attached.
 *
 * Extension point: once the cloud records which node holds each image, a study
 * held at a *different* site of the same clinic is a legitimate cloud read
 * rather than a failure. That fact is per-study, not per-origin, so it arrives
 * as an additional argument here (`heldElsewhere`) and returns an
 * `'other-site'` state between rules 4 and 5 — no restructuring of the phases
 * or of the store. Not built yet; do not infer it from origin phases.
 */
export function deriveLocalSourceState(phases, override = null) {
    if (override === 'force-local')
        return 'local';
    if (override === 'force-cloud')
        return 'cloud';
    if (phases.length === 0)
        return 'cloud';
    if (phases.includes('reachable'))
        return 'local';
    if (phases.includes('probing'))
        return 'probing';
    if (phases.includes('permission-denied'))
        return 'permission-denied';
    return 'cloud-fallback';
}
const originPhases = new Map();
const listeners = new Set();
/**
 * `undefined` means "not read from storage yet". Cached because the data path
 * consults the override on every single image (via ./index's isLocalEnabled),
 * and that must not be a storage hit per request.
 */
let overrideCache;
function readStoredOverride() {
    try {
        const raw = sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
        const stored = raw === 'force-local' || raw === 'force-cloud' ? raw : null;
        if (stored && overrideBelongsToAnotherWindow()) {
            // Cleared as well as ignored: this window's next reload DOES look like a
            // reload, so leaving the copy in place would let it be adopted after all.
            sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
            return null;
        }
        return stored;
    }
    catch {
        // A browser with site data blocked throws on sessionStorage. A diagnostic
        // must never be the thing that breaks image loading.
        return null;
    }
}
/** The override in force for this tab. Cheap enough for the hot path. */
export function getDiagnosticOverride() {
    if (overrideCache === undefined)
        overrideCache = readStoredOverride();
    return overrideCache;
}
/** Pin (or unpin) the image path for this tab only, and notify subscribers. */
export function setDiagnosticOverride(override) {
    overrideCache = override;
    try {
        if (override === null)
            sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
        else
            sessionStorage.setItem(OVERRIDE_STORAGE_KEY, override);
    }
    catch {
        // Unpersisted is still better than unapplied — the in-memory cache holds
        // for this tab's lifetime either way.
    }
    recompute();
}
function sameOrigins(a, b) {
    return a.length === b.length && a.every((origin, i) => origin === b[i]);
}
function buildSnapshot() {
    const override = getDiagnosticOverride();
    return {
        state: deriveLocalSourceState([...originPhases.values()], override),
        override,
        origins: [...originPhases.keys()],
    };
}
let snapshot = buildSnapshot();
/**
 * Rebuild the snapshot, keeping the previous object when nothing observable
 * changed — `useSyncExternalStore` compares snapshots by identity, so handing
 * back a fresh object on every probe tick would re-render the viewer chrome for
 * no reason (and React throws outright if a render-time read keeps changing).
 */
function recompute() {
    const next = buildSnapshot();
    if (next.state === snapshot.state &&
        next.override === snapshot.override &&
        sameOrigins(next.origins, snapshot.origins)) {
        return;
    }
    snapshot = next;
    for (const listener of listeners)
        listener();
}
/** Identity-stable snapshot for useSyncExternalStore. */
export function getLocalGatewayStatus() {
    return snapshot;
}
/** Subscribe to status changes. Returns the unsubscribe callback. */
export function subscribeLocalGatewayStatus(onChange) {
    listeners.add(onChange);
    return () => {
        listeners.delete(onChange);
    };
}
/** Record (or update) one origin's phase. Called only from ./index. */
export function setOriginPhase(origin, phase) {
    if (!origin)
        return;
    if (originPhases.get(origin) === phase)
        return;
    originPhases.set(origin, phase);
    recompute();
}
/** This origin's phase, or undefined if this window has never met it. */
export function getOriginPhase(origin) {
    return originPhases.get(origin);
}
/**
 * Every gateway origin this window has met — what a retry re-probes.
 *
 * A fresh array, not the snapshot's: `recompute` compares snapshots by identity
 * and the origin list element-wise, so handing out the live array would let a
 * caller that sorts or splices it corrupt the comparison and silently stop the
 * indicator from re-rendering.
 */
export function knownLocalGatewayOrigins() {
    return [...originPhases.keys()];
}
/** Full reset, including the override. Paired with ./index's reset, for tests. */
export function clearLocalGatewayStatus() {
    originPhases.clear();
    overrideCache = null;
    try {
        sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
    recompute();
}
